package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.auth.AuthenticatedSession
import com.royceecoleman.bill.auth.displayName
import com.royceecoleman.bill.settings.deleteManagedAiModel
import com.royceecoleman.bill.settings.fetchManagedAiModels
import com.royceecoleman.bill.settings.saveManagedAiModel
import com.royceecoleman.bill.settings.setDefaultManagedAiModel
import com.royceecoleman.bill.shared.aichat.BILL_SUPPORTED_MODEL_PROVIDERS
import com.royceecoleman.bill.shared.aichat.ManagedAiModelDto
import com.royceecoleman.bill.shared.aichat.sortedForManagement
import com.royceecoleman.bill.shared.aichat.visibleManagedModels
import com.varabyte.kobweb.core.Page
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

const val AI_MODEL_SETTINGS_DASHBOARD_DESCRIPTION =
    "Manage the Spring AI chat models bill-ai can use and pick the active default."

@Page
@Composable
fun settingsPage() {
    GuardedAdminPage(
        checkingMessage = "Loading Bill settings for your authenticated role.",
        redirectingToLoginMessage = "You need to sign in before managing Bill settings.",
        redirectingToMapMessage = "Customer accounts cannot manage Bill settings.",
    ) { currentSession ->
        SettingsContent(currentSession)
    }
}

@Composable
private fun SettingsContent(currentSession: AuthenticatedSession) {
    CardPage(
        title = "Bill Settings",
        supportingText =
            "Signed in as ${currentSession.username} (${currentSession.role.displayName()}). " +
                "Changes take effect immediately without restarting bill-ai.",
    ) {
        AiModelManagementCard()
    }
}

@Composable
private fun AiModelManagementCard() {
    val coroutineScope = rememberCoroutineScope()
    var models by remember { mutableStateOf(emptyList<ManagedAiModelDto>()) }
    var showDeprecated by remember { mutableStateOf(false) }
    var isBusy by remember { mutableStateOf(false) }
    var editor by remember { mutableStateOf<ManagedModelEditorState?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var statusMessage by remember { mutableStateOf<String?>(null) }

    suspend fun runOperation(
        description: String,
        block: suspend () -> Unit,
    ) {
        isBusy = true
        errorMessage = null
        try {
            block()
            models = fetchManagedAiModels()
            statusMessage = description
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (failure: Throwable) {
            statusMessage = null
            errorMessage = failure.message ?: "Unable to $description."
        } finally {
            isBusy = false
        }
    }

    LaunchedEffect(Unit) {
        runOperation("Loaded AI chat models.") { }
    }

    Div(
        attrs = {
            style {
                property("display", "grid")
                property("gap", "1rem")
                property("margin-top", "1.5rem")
            }
        },
    ) {
        H2(
            attrs = {
                style {
                    property("margin", "0")
                    property("font-size", "1.25rem")
                }
            },
        ) {
            Text("AI model management")
        }
        P(
            attrs = {
                style {
                    property("margin", "0")
                    property("color", "#475569")
                    property("line-height", "1.6")
                }
            },
        ) {
            Text(AI_MODEL_SETTINGS_DASHBOARD_DESCRIPTION)
        }

        errorMessage?.let { message -> FeedbackBanner(message = message, isError = true) }
        if (errorMessage == null) {
            statusMessage?.let { message -> FeedbackBanner(message = message, isError = false) }
        }

        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("gap", "0.75rem")
                    property("flex-wrap", "wrap")
                    property("align-items", "center")
                }
            },
        ) {
            SettingsButton(
                label = "Add model",
                enabled = !isBusy,
                onClick = { editor = emptyManagedModelEditorState() },
            )
            SettingsButton(
                label = "Refresh",
                enabled = !isBusy,
                onClick = { coroutineScope.launch { runOperation("Loaded AI chat models.") { } } },
            )
            Label(
                attrs = {
                    style {
                        property("display", "flex")
                        property("gap", "0.4rem")
                        property("align-items", "center")
                        property("color", "#334155")
                    }
                },
            ) {
                Input(type = InputType.Checkbox) {
                    checked(showDeprecated)
                    onInput { event -> showDeprecated = event.value }
                }
                Text("Show deprecated")
            }
        }

        val visibleModels = models.visibleManagedModels(showDeprecated).sortedForManagement()
        if (visibleModels.isEmpty()) {
            P(attrs = { style { property("color", "#64748b") } }) {
                Text(if (isBusy) "Loading AI chat models…" else "No AI chat models are configured yet.")
            }
        }

        visibleModels.forEach { model ->
            ManagedModelRow(
                model = model,
                enabled = !isBusy,
                onEdit = { editor = model.toEditorState() },
                onSelectDefault = {
                    coroutineScope.launch {
                        runOperation("Set '${model.name}' as the default chat model.") {
                            setDefaultManagedAiModel(model.id)
                        }
                    }
                },
                onDelete = {
                    val blockedReason = deleteBlockedReason(model)
                    if (blockedReason != null) {
                        statusMessage = null
                        errorMessage = blockedReason
                    } else {
                        coroutineScope.launch {
                            runOperation("Deleted '${model.name}'.") { deleteManagedAiModel(model.id) }
                        }
                    }
                },
            )
        }

        editor?.let { editorState ->
            ManagedModelEditor(
                state = editorState,
                enabled = !isBusy,
                onStateChange = { update -> editor = editor?.let(update) },
                onCancel = { editor = null },
                onSave = {
                    val current = editor
                    val validationError = current?.validationError()
                    if (current == null) {
                        editor = null
                    } else if (validationError != null) {
                        statusMessage = null
                        errorMessage = validationError
                    } else {
                        editor = null
                        coroutineScope.launch {
                            runOperation("Saved '${current.name.trim()}'.") {
                                saveManagedAiModel(current.toRequest())
                            }
                        }
                    }
                },
            )
        }
    }
}

@Composable
private fun FeedbackBanner(
    message: String,
    isError: Boolean,
) {
    Div(
        attrs = {
            style {
                property("padding", "0.85rem 1rem")
                property("border-radius", "0.75rem")
                property("background", if (isError) "#fee2e2" else "#dcfce7")
                property("color", if (isError) "#b91c1c" else "#166534")
            }
        },
    ) {
        Text(message)
    }
}

@Composable
private fun ManagedModelRow(
    model: ManagedAiModelDto,
    enabled: Boolean,
    onEdit: () -> Unit,
    onSelectDefault: () -> Unit,
    onDelete: () -> Unit,
) {
    Div(
        attrs = {
            style {
                property("padding", "0.9rem 1rem")
                property("border", "1px solid #cbd5e1")
                property("border-radius", "0.85rem")
                property("background", if (model.isDefault) "#eff6ff" else "#f8fafc")
                property("display", "grid")
                property("gap", "0.5rem")
            }
        },
    ) {
        Div(attrs = { style { property("font-weight", "700") } }) {
            Text("${model.name} (${model.id})")
        }
        Div(attrs = { style { property("color", "#475569") } }) {
            Text(managedModelSummary(model))
        }
        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("gap", "0.6rem")
                    property("flex-wrap", "wrap")
                }
            },
        ) {
            SettingsButton(label = "Edit", enabled = enabled, onClick = onEdit)
            SettingsButton(
                label = "Make default",
                enabled = enabled && !model.isDefault && !model.deprecated,
                onClick = onSelectDefault,
            )
            SettingsButton(
                label = "Delete",
                enabled = enabled && !model.isDefault,
                onClick = onDelete,
            )
        }
    }
}

@Composable
private fun ManagedModelEditor(
    state: ManagedModelEditorState,
    enabled: Boolean,
    onStateChange: ((ManagedModelEditorState) -> ManagedModelEditorState) -> Unit,
    onCancel: () -> Unit,
    onSave: () -> Unit,
) {
    Div(
        attrs = {
            style {
                property("padding", "1rem")
                property("border", "1px solid #2563eb")
                property("border-radius", "0.85rem")
                property("display", "grid")
                property("gap", "0.75rem")
            }
        },
    ) {
        Div(attrs = { style { property("font-weight", "700") } }) {
            Text(if (state.isExisting) "Edit ${state.id}" else "Add a chat model")
        }

        EditorField(
            label = "Model id",
            value = state.id,
            enabled = enabled && !state.isExisting,
            onValueChange = { value -> onStateChange { current -> current.copy(id = value) } },
        )
        EditorField(
            label = "Display name",
            value = state.name,
            enabled = enabled,
            onValueChange = { value -> onStateChange { current -> current.copy(name = value) } },
        )
        Label(attrs = { style { property("display", "grid"); property("gap", "0.3rem") } }) {
            Text("Provider")
            Select(
                attrs = {
                    if (!enabled) disabled()
                    onChange { event -> onStateChange { current -> current.copy(provider = event.value.orEmpty()) } }
                },
            ) {
                BILL_SUPPORTED_MODEL_PROVIDERS.forEach { provider ->
                    Option(
                        value = provider,
                        attrs = { if (provider == state.provider) attr("selected", "selected") },
                    ) {
                        Text(provider)
                    }
                }
            }
        }
        EditorField(
            label = "Deployment name",
            value = state.deploymentName,
            enabled = enabled,
            onValueChange = { value -> onStateChange { current -> current.copy(deploymentName = value) } },
        )
        EditorField(
            label = "Temperature",
            value = state.temperature,
            enabled = enabled,
            onValueChange = { value -> onStateChange { current -> current.copy(temperature = value) } },
        )

        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("gap", "0.6rem")
                }
            },
        ) {
            SettingsButton(label = "Save", enabled = enabled, onClick = onSave)
            SettingsButton(label = "Cancel", enabled = enabled, onClick = onCancel)
        }
    }
}

@Composable
private fun EditorField(
    label: String,
    value: String,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
) {
    Label(attrs = { style { property("display", "grid"); property("gap", "0.3rem") } }) {
        Text(label)
        Input(type = InputType.Text) {
            value(value)
            if (!enabled) disabled()
            onInput { event -> onValueChange(event.value) }
            style {
                property("padding", "0.6rem 0.75rem")
                property("border", "1px solid #cbd5e1")
                property("border-radius", "0.6rem")
            }
        }
    }
}

@Composable
private fun SettingsButton(
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Button(
        attrs = {
            if (!enabled) disabled()
            onClick { onClick() }
            style {
                property("padding", "0.6rem 1rem")
                property("border", "0")
                property("border-radius", "0.6rem")
                property("background", if (enabled) "#2563eb" else "#94a3b8")
                property("color", "#ffffff")
                property("font-weight", "700")
                property("cursor", if (enabled) "pointer" else "not-allowed")
            }
        },
    ) {
        Text(label)
    }
}
